import React, { useState, useCallback, useEffect } from 'react';
import { useData } from 'vike-react/useData';
import {
  MapPin, Phone, MessageCircle, ShieldCheck, Building2,
  Calendar, Home, ArrowLeft, Share, Crown, Headphones
} from 'lucide-react';
import type { AgentPageData } from './+data';

// SSR-safe layout components
import HeaderSSR from '../../../components/HeaderSSR';
import FooterSSR from '../../../components/FooterSSR';
import { getSpotlightLabel } from '../../../lib/badges';

// =============================================
// SPA SHELL COMPONENT
// For agent SPA sub-routes like /agent/new-listing
// =============================================
function SpaShell() {
  const [AppComponent, setAppComponent] = useState<React.ComponentType | null>(null);

  useEffect(() => {
    import('../../../../App').then((mod) => {
      setAppComponent(() => mod.default);
    });
  }, []);

  if (!AppComponent) {
    return <div className="min-h-screen" />;
  }

  return <AppComponent />;
}

// =============================================
// FORMAT HELPERS (SSR-safe)
// =============================================
function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
  });
}

function formatPrice(price: number, currency: string): string {
  return `${currency} ${price.toLocaleString()}`;
}

// =============================================
// MAIN COMPONENT
// =============================================
export default function Page() {
  const data = useData<AgentPageData>();

  // If this is an SPA sub-route, render the SPA shell instead
  if (data.isSpaRoute) {
    return <SpaShell />;
  }

  const { agent, listings } = data;

  // =============================================
  // CLIENT STATE (hydration)
  // =============================================
  const [showCopied, setShowCopied] = useState(false);

  const handleShare = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (navigator.share) {
      navigator.share({
        title: `${agent.full_name} - Afriqahome Agent`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 2000);
    }
  }, [agent.full_name]);

  // Use agent's custom background, or first listing image, or a CSS gradient fallback
  const heroImage = agent.background_image || (listings[0]?.image_url) || null;

  return (
    <div className="min-h-screen bg-[#F9FAFB]">

      {/* =============================================
          HEADER – SSR-safe Navbar
          ============================================= */}
      <HeaderSSR />

      {/* =============================================
          HERO COVER
          ============================================= */}
      <div className="relative h-72 overflow-hidden">
        {heroImage ? (
          <div
            className="absolute inset-0 bg-cover bg-center blur-sm scale-105"
            style={{ backgroundImage: `url(${heroImage})` }}
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-brand-primary via-brand-primary/80 to-brand-accent" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/40 to-gray-900/80" />

        {/* Back Button */}
        <a
          href="/"
          className="absolute top-24 left-4 md:left-8 z-30 flex items-center gap-2 text-white/80 hover:text-white transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">Back</span>
        </a>

        {/* Share Button */}
        <button
          onClick={handleShare}
          className="absolute top-24 right-4 md:right-8 z-30 p-2 bg-white/10 backdrop-blur-sm rounded-full text-white/80 hover:text-white hover:bg-white/20 transition-all"
        >
          <Share size={18} />
        </button>

        {/* Copied toast */}
        {showCopied && (
          <div className="absolute top-24 right-16 md:right-20 z-30 px-3 py-1.5 bg-white text-gray-900 text-xs font-medium rounded-full shadow-lg">
            Link copied!
          </div>
        )}
      </div>

      {/* =============================================
          FLOATING PROFILE CARD
          ============================================= */}
      <div className="relative z-20 -mt-24 px-4 md:px-8 max-w-4xl mx-auto">
        <div className="bg-white/80 backdrop-blur-xl rounded-[2.5rem] shadow-[0_20px_40px_-15px_rgba(0,0,0,0.1)] border border-gray-100 p-8 md:p-10">
          <div className="flex flex-col md:flex-row gap-6 md:gap-8">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <div className="relative">
                <img
                  src={agent.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(agent.full_name || 'Agent')}&size=128&background=2F5233&color=fff`}
                  alt={agent.full_name || 'Agent'}
                  className="w-28 h-28 md:w-32 md:h-32 rounded-full object-cover border-4 border-white shadow-lg"
                />
                {agent.is_verified && (
                  <div className="absolute -bottom-1 -right-1 bg-blue-500 text-white p-1.5 rounded-full shadow-lg">
                    <ShieldCheck size={18} />
                  </div>
                )}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1">
              <div className="flex flex-wrap items-start gap-3 mb-3">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                  {agent.full_name || 'Agent'}
                </h1>
                {agent.tier === 'dominate' && (
                  <>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold rounded-full shadow-sm">
                      <Crown size={12} />
                      VIP Agent
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-gray-900 text-xs font-semibold rounded-full">
                      <Headphones size={12} />
                      Priority Support
                    </span>
                  </>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-4 text-gray-600 mb-4">
                <span className="inline-flex items-center gap-2">
                  {/* AGENCY-10: not tier-gated. The logo renders whenever branding
                      resolves to an agency — attribution is the trust product,
                      Dominate sells ranking/cap/chat rate, not visibility. */}
                  {agent.agency_logo_url ? (
                    <img
                      src={agent.agency_logo_url}
                      alt={agent.agency_name || 'Agency Logo'}
                      className="h-6 w-auto max-w-[80px] object-contain"
                    />
                  ) : (
                    <Building2 size={16} className="text-gray-400" />
                  )}
                  {agent.is_independent
                    ? 'Independent Agent'
                    : agent.agency_name || 'Real Estate Agent'
                  }
                </span>
                {agent.location && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin size={16} className="text-gray-400" />
                    {agent.location}
                  </span>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                {agent.phone && (
                  <>
                    <a
                      href={`https://wa.me/${agent.phone.replace(/\s+/g, '').replace(/^\+/, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#25D366] text-white font-medium rounded-full hover:bg-[#20bd5a] transition-colors shadow-sm"
                    >
                      <MessageCircle size={18} />
                      WhatsApp
                    </a>
                    <a
                      href={`tel:${agent.phone}`}
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white font-medium rounded-full hover:bg-gray-800 transition-colors shadow-sm"
                    >
                      <Phone size={18} />
                      Call
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =============================================
          BIO & STATS SECTION
          ============================================= */}
      <div className="px-4 md:px-8 max-w-4xl mx-auto mt-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Bio */}
          <div className="md:col-span-2 bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">About</h2>
            {agent.bio ? (
              <p className="text-gray-600 leading-relaxed">{agent.bio}</p>
            ) : (
              <p className="text-gray-400 italic">No bio provided yet.</p>
            )}

            {/* Verification badges */}
            {agent.is_verified && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <div className="flex items-center gap-2 text-blue-600 text-sm">
                  <ShieldCheck size={16} />
                  <span className="font-medium">Verified Agent</span>
                  <span className="text-gray-400">- Identity confirmed by Afriqahome</span>
                </div>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Stats</h2>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-brand-primary/10 rounded-full flex items-center justify-center">
                  <Home size={18} className="text-brand-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{listings.length}</p>
                  <p className="text-sm text-gray-500">Active Listings</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <Calendar size={18} className="text-gray-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {formatDate(agent.created_at)}
                  </p>
                  <p className="text-sm text-gray-500">Member Since</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =============================================
          LISTINGS SECTION
          ============================================= */}
      <div className="px-4 md:px-8 max-w-4xl mx-auto mt-8 pb-24 md:pb-12">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            Active Listings
            <span className="ml-2 text-gray-400 font-normal">({listings.length})</span>
          </h2>
        </div>

        {listings.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center">
            <Home size={40} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No active listings at the moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {listings.map((listing) => (
              <a
                key={listing.id}
                href={`/property/${listing.slug || listing.id}`}
                className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={listing.image_url || ''}
                    alt={listing.address || listing.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {listing.is_spotlight && (
                    <div className="absolute top-3 left-3 px-2.5 py-1 bg-amber-400 text-white text-xs font-semibold rounded-full">
                      {getSpotlightLabel()}
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <p className="text-lg font-bold text-gray-900 mb-1">
                    {formatPrice(listing.price, listing.currency)}
                  </p>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-1">
                    {listing.address || listing.city}
                  </p>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>{listing.beds ?? '—'} beds</span>
                    <span>-</span>
                    <span>{listing.baths ?? '—'} baths</span>
                    <span>-</span>
                    <span>{listing.area_sqm ?? '—'} sqm</span>
                  </div>

                  {/* AGENCY-10: the agency stamped on THIS listing — which can
                      differ from the header when the agent has changed firms. */}
                  {(listing.agency_name || listing.agency_logo_url) && (
                    <div className="flex items-center justify-between gap-2 pt-2 mt-2 border-t border-gray-100">
                      <span className="text-xs text-gray-600 truncate">
                        {listing.agency_name}
                      </span>
                      {listing.agency_logo_url && (
                        <img
                          src={listing.agency_logo_url}
                          alt={listing.agency_name || 'Agency'}
                          className="h-5 max-w-[56px] object-contain flex-shrink-0"
                          width={56}
                          height={20}
                          loading="lazy"
                        />
                      )}
                    </div>
                  )}
                </div>
              </a>
            ))}
          </div>
        )}
      </div>

      {/* =============================================
          FOOTER – SSR-safe site footer
          ============================================= */}
      <FooterSSR />

    </div>
  );
}
